// src/utils/tokenManager.js
import { logError } from "./logger";

/**
 * In-memory access token storage
 * Security: Stored in memory (not localStorage) to prevent XSS attacks
 * Trade-off: Token lost on page refresh, but restored via httpOnly cookie
 */

let accessToken = null;

/**
 * Get current access token
 * @returns {string|null} Access token or null if not set
 */
export const getAccessToken = () => accessToken;

/**
 * Store access token in memory
 * @param {string} token - JWT access token from backend
 */
export const setAccessToken = (token) => {
  accessToken = token;
};

/**
 * Clear access token from memory
 */
export const clearAccessToken = () => {
  accessToken = null;
};

/**
 * Check if current token is expired
 * @returns {boolean} True if token is expired or doesn't exist
 */
export const isTokenExpired = () => {
  if (!accessToken) return true;

  try {
    const decoded = parseJWT(accessToken);
    return decoded.exp * 1000 < Date.now();
  } catch (error) {
    logError("Error parsing JWT:", error);
    return true;
  }
};

/**
 * Parse JWT token to extract payload
 * @param {string} token - JWT token to parse
 * @returns {object} Decided JWT payload
 */
export const parseJWT = (token) => {
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      window
        .atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join(""),
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    throw new Error("Invalid JWT token");
  }
};

/**
 * Get user ID from current token
 * @return {string|null} User ID or null if token doesn't exist
 */
export const getUserIdFromToken = () => {
  if (!accessToken) return null;

  try {
    const decoded = parseJWT(accessToken);
    return decoded.userId || decoded.id || decoded.sub;
  } catch (error) {
    return null;
  }
};
